import { useState } from "react";
import { AlertTriangle, CalendarClock, CheckCircle2, CircleDashed, GraduationCap, Clock, FileText, Landmark, Lightbulb, LogOut, MessageSquare, Receipt, TrendingDown, TrendingUp } from "lucide-react";
import { toast } from "sonner";
import { SiteLayout } from "@/components/SiteChrome";
import { MarketingCalendar } from "@/components/MarketingCalendar";
import { trpc } from "@/lib/trpc";

/**
 * The client engagement dashboard.
 *
 * Every panel is driven by the `visibility` object the server returns, which
 * was computed from the signed-in user's role before any data was fetched. A
 * bookkeeper's payload does not contain the executed POA; it is not hidden in
 * the browser, it was never sent.
 */

const money = (cents: number | null | undefined) =>
  cents === null || cents === undefined
    ? "—"
    : new Intl.NumberFormat("en-US", { style: "currency", currency: "USD", minimumFractionDigits: cents % 100 === 0 ? 0 : 2 }).format(cents / 100);

const date = (value: string | Date | null | undefined) =>
  value ? new Date(value).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" }) : "—";

const DOCUMENT_LABEL: Record<string, string> = {
  management_agreement: "Management agreement",
  consulting_agreement: "Consulting agreement",
  nda: "Non-disclosure agreement",
  poa: "Power of attorney",
  w9: "W-9",
  addendum: "Addendum",
  other: "Document",
};

const SUGGESTION_CATEGORY: Record<string, string> = {
  marketing: "Marketing",
  operations: "Operations",
  retention: "Retention",
  revenue: "Revenue",
  facility: "Facility",
  staffing: "Staffing",
  other: "General",
};

export default function ClientPortal() {
  const session = trpc.clientPortal.session.useQuery();
  const utils = trpc.useUtils();
  const logout = trpc.clientPortal.logout.useMutation({ onSuccess: () => window.location.assign("/clients/login") });
  const stage = session.data?.stage;

  const refresh = () => void utils.clientPortal.dashboard.invalidate();
  const onError = (error: { message: string }) => toast.error("That did not save", { description: error.message });

  // Autopay. The mutation hands back a Stripe URL and the browser leaves; it
  // returns nothing that says "verified", because at that moment nothing is.
  const startAutopay = trpc.clientPortal.startAutopay.useMutation({
    onSuccess: result => window.location.assign(result.url),
    onError,
  });
  const billingPortal = trpc.clientPortal.billingPortal.useMutation({
    onSuccess: result => window.location.assign(result.url),
    onError,
  });

  const saveCampaign = trpc.clientPortal.saveCampaign.useMutation({ onSuccess: refresh, onError });
  const updateCampaign = trpc.clientPortal.updateCampaign.useMutation({ onSuccess: refresh, onError });
  const deleteCampaign = trpc.clientPortal.deleteCampaign.useMutation({ onSuccess: refresh, onError });
  const answerSuggestion = trpc.clientPortal.answerSuggestion.useMutation({
    onSuccess: () => { toast.success("Thank you — your answer is with your engagement lead."); refresh(); },
    onError,
  });
  const postNote = trpc.clientPortal.postNote.useMutation({
    onSuccess: () => { toast.success("Note sent."); refresh(); },
    onError,
  });

  // The gate is the session stage, not a flag on the data. A half-verified
  // session is routed back into the flow rather than shown an empty shell.
  const dashboard = trpc.clientPortal.dashboard.useQuery(undefined, { enabled: stage === "active" });

  if (session.isLoading) {
    return <SiteLayout><div className="cp-page cp-page--narrow"><section className="section"><div className="site-container">Loading…</div></section></div></SiteLayout>;
  }
  if (stage === "awaiting_second_factor") { window.location.assign("/clients/verify"); return null; }
  if (stage === "enrolment_required") { window.location.assign("/clients/secure"); return null; }
  if (stage !== "active" || !dashboard.data) {
    if (stage !== "active") {
      return (
        <SiteLayout>
          <div className="cp-page cp-page--narrow">
            <section className="section">
              <div className="site-container cp-auth">
                <p className="eyebrow">Altitude client portal</p>
                <h1>Please sign in.</h1>
                <a className="button button--coral button--full" href="/clients/login">Email me a sign-in link</a>
              </div>
            </section>
          </div>
        </SiteLayout>
      );
    }
    return <SiteLayout><div className="cp-page"><section className="section"><div className="site-container">Loading your engagement…</div></section></div></SiteLayout>;
  }

  const d = dashboard.data;
  const v = d.visibility;
  const identity = session.data?.identity;

  return (
    <SiteLayout>
      <div className="cp-page">
        <section className="cp-portal-top">
          <div className="site-container">
            <div>
              <p className="eyebrow eyebrow--light">{d.client.reference} · {d.client.engagementType} engagement</p>
              <h1>{d.client.companyName}</h1>
              <p>{identity?.name}{identity?.role ? ` · ${identity.role}` : ""}</p>
            </div>
            <button className="button button--outline-light" onClick={() => logout.mutate()}>
              <LogOut size={15} />Sign out
            </button>
          </div>
        </section>

        {/* Training account. First thing on the page and deliberately loud:
            a trainee who mistakes sample data for their own club's numbers
            will act on figures that were invented by a seeder. */}
        {d.client.isDemo && (
          <section className="cp-banner cp-banner--demo">
            <div className="site-container">
              <GraduationCap size={19} />
              <div>
                <strong>Training account — sample data.</strong>
                <span>
                  Everything here is made up for training. Nothing you do sends an email, charges a
                  card or reaches a real client, and the data is rebuilt every night.
                </span>
              </div>
            </div>
          </section>
        )}

        {/* The autopay gate. Shown to everyone because it blocks the
            engagement, but only roles that can act on it get the button. */}
        {!d.client.paymentMethodVerified && (
          <section className="cp-banner cp-banner--warn">
            <div className="site-container">
              <AlertTriangle size={19} />
              <div>
                <strong>Autopay is not yet set up.</strong>
                <span>Services cannot begin until a verified payment method is on file.</span>
              </div>
              {v.manageBilling && (
                <button
                  className="button button--coral"
                  disabled={startAutopay.isPending}
                  onClick={() => startAutopay.mutate()}
                >
                  {startAutopay.isPending ? "Opening Stripe…" : "Set up autopay"}
                </button>
              )}
            </div>
          </section>
        )}

        {/* Suspension. Rendered before anything else and stated plainly: a
            client whose services have stopped should not have to read past a
            progress bar to find that out. The panels it withholds were
            already emptied server-side, so this explains an absence rather
            than creating one. */}
        {d.restriction?.suspended && (
          <section className="cp-banner cp-banner--stop">
            <div className="site-container">
              <AlertTriangle size={19} />
              <div>
                <strong>{d.restriction.headline}</strong>
                <span>{d.restriction.detail}</span>
              </div>
              {v.manageBilling && (
                <a className="button button--coral" href="#billing">Settle the balance</a>
              )}
            </div>
          </section>
        )}

        {/* An arrangement in force. Shown because a client who agreed terms
            and still sees past-due figures will otherwise call to ask. */}
        {d.arrangement && (
          <section className="cp-banner cp-banner--hold">
            <div className="site-container">
              <CalendarClock size={19} />
              <div>
                <strong>A payment arrangement is in place.</strong>
                <span>
                  {d.arrangement.terms}
                  {d.arrangement.until ? ` Services continue through ${date(d.arrangement.until)}.` : ""}
                </span>
              </div>
            </div>
          </section>
        )}

        {/* Delinquency. Suspension is stated plainly rather than softened. */}
        {v.billing && d.standing && d.standing.stage !== "current" && !d.restriction?.suspended && (
          <section className={`cp-banner ${d.standing.suspended ? "cp-banner--stop" : "cp-banner--warn"}`}>
            <div className="site-container">
              <AlertTriangle size={19} />
              <div>
                <strong>
                  {d.standing.suspended
                    ? "Services are suspended for non-payment."
                    : `${d.standing.stageLabel} — ${money(d.standing.pastDueCents)} past due`}
                </strong>
                <span>
                  {d.standing.suspended
                    ? "Settle the outstanding balance to resume. Contact us if you believe this is in error."
                    : d.standing.nextThreshold
                      ? `${d.standing.nextThreshold.label} in ${d.standing.nextThreshold.inDays} day${d.standing.nextThreshold.inDays === 1 ? "" : "s"}.`
                      : "Please settle at your earliest convenience."}
                </span>
              </div>
            </div>
          </section>
        )}

        <section className="section">
          <div className="site-container">
            {/* Headline figures */}
            <div className="cp-tiles">
              <article>
                <span className="cp-tiles__label"><TrendingUp size={13} />Engagement progress</span>
                <strong>{d.progress.overallPercent}%</strong>
                <small>{d.progress.completeMilestones} of {d.progress.totalMilestones} milestones</small>
              </article>
              <article>
                <span className="cp-tiles__label"><Clock size={13} />Current phase</span>
                <strong className="cp-tiles__text">{d.phases.find(p => p.phase === d.progress.currentPhase)?.label ?? "—"}</strong>
                <small>{d.progress.awaitingClient} item{d.progress.awaitingClient === 1 ? "" : "s"} waiting on you</small>
              </article>
              {v.commercialTerms && (
                <article>
                  <span className="cp-tiles__label"><Receipt size={13} />Remaining contract value</span>
                  <strong>{money(d.contract?.remainingCents)}</strong>
                  <small>of {money(d.contract?.contractValueCents)} total</small>
                </article>
              )}
              {v.billing && (
                <article className={d.standing?.pastDueCents ? "is-alert" : undefined}>
                  <span className="cp-tiles__label"><Receipt size={13} />Balance due</span>
                  <strong>{money(d.standing?.outstandingCents ?? 0)}</strong>
                  <small>{d.standing?.pastDueCents ? `${money(d.standing.pastDueCents)} past due` : "Nothing past due"}</small>
                </article>
              )}
              {v.commercialTerms && (
                <article>
                  <span className="cp-tiles__label"><CalendarClock size={13} />Contract term</span>
                  <strong className="cp-tiles__text">{date(d.client.contractStartAt)}</strong>
                  <small>
                    to {date(d.client.contractEndAt)}
                    {d.contract?.daysRemaining !== null && d.contract?.daysRemaining !== undefined
                      ? ` · ${d.contract.daysRemaining} days left`
                      : ""}
                  </small>
                </article>
              )}
            </div>

            <div className="cp-columns">
              <div>
                {/* Phase progress */}
                <section className="cp-card">
                  <header><h2>Project phases</h2></header>
                  <ol className="cp-phases">
                    {d.phases.map(phase => (
                      <li key={phase.phase} data-state={phase.state}>
                        <div className="cp-phases__head">
                          <span>{phase.label}</span>
                          <em>{phase.total === 0 ? "Not scoped" : `${phase.complete}/${phase.total}`}</em>
                        </div>
                        <div className="cp-bar"><i style={{ width: `${phase.percent}%` }} /></div>
                        {(phase.blocked > 0 || phase.awaitingClient > 0) && (
                          <small>
                            {phase.blocked > 0 && `${phase.blocked} blocked`}
                            {phase.blocked > 0 && phase.awaitingClient > 0 && " · "}
                            {phase.awaitingClient > 0 && `${phase.awaitingClient} waiting on you`}
                          </small>
                        )}
                      </li>
                    ))}
                  </ol>
                </section>

                {/* Updates */}
                <section className="cp-card">
                  <header><h2>Updates</h2></header>
                  {d.updates.length === 0 ? (
                    <p className="cp-empty">No updates posted yet.</p>
                  ) : (
                    <ul className="cp-updates">
                      {d.updates.map(update => (
                        <li key={update.id} data-pinned={update.pinned || undefined}>
                          <div><strong>{update.title}</strong><small>{date(update.publishedAt)} · {update.authorName}</small></div>
                          <p>{update.body}</p>
                        </li>
                      ))}
                    </ul>
                  )}
                </section>

                {/* Suggestions. Separate from updates because each one carries
                    a decision, and the client's answer is part of the record. */}
                <section className="cp-card" id="suggestions">
                  <header>
                    <h2>Suggestions from Altitude</h2>
                    {d.openSuggestions > 0 && <span className="cp-count">{d.openSuggestions} awaiting you</span>}
                  </header>
                  {d.suggestions.length === 0 ? (
                    <p className="cp-empty">
                      <Lightbulb size={15} /> Nothing here yet. As we work through your numbers we will
                      post ideas for you to accept or decline.
                    </p>
                  ) : (
                    <ul className="cp-suggestions">
                      {d.suggestions.map(suggestion => (
                        <li key={suggestion.id} data-status={suggestion.status} data-priority={suggestion.priority}>
                          <div className="cp-suggestions__head">
                            <span className="cp-tag">{SUGGESTION_CATEGORY[suggestion.category] ?? "General"}</span>
                            {suggestion.priority === "high" && <span className="cp-tag cp-tag--urgent">Priority</span>}
                            <em>{suggestion.status === "open" ? "Awaiting your decision" : suggestion.status}</em>
                          </div>
                          <strong>{suggestion.title}</strong>
                          <p>{suggestion.body}</p>
                          <small>{suggestion.authorName} · {date(suggestion.createdAt)}</small>

                          {suggestion.clientResponse && (
                            <blockquote className="cp-suggestions__reply">{suggestion.clientResponse}</blockquote>
                          )}

                          {suggestion.status === "open" && v.answerSuggestions && (
                            <SuggestionAnswer
                              busy={answerSuggestion.isPending}
                              onAnswer={(status, response) =>
                                answerSuggestion.mutate({ id: suggestion.id, status, response })
                              }
                            />
                          )}
                        </li>
                      ))}
                    </ul>
                  )}
                </section>
              </div>

              <div>
                {/* Pending items */}
                <section className="cp-card">
                  <header><h2>Waiting on you</h2></header>
                  {d.pendingItems.length === 0 ? (
                    <p className="cp-empty"><CheckCircle2 size={15} /> Nothing outstanding. We will post here when we need something.</p>
                  ) : (
                    <ul className="cp-pending">
                      {d.pendingItems.map(item => (
                        <li key={item.id}>
                          <CircleDashed size={14} />
                          <div><strong>{item.title}</strong>{item.detail && <small>{item.detail}</small>}{item.dueAt && <em>Due {date(item.dueAt)}</em>}</div>
                        </li>
                      ))}
                    </ul>
                  )}
                </section>

                {v.documents && (
                  <section className="cp-card">
                    <header><h2>Documents</h2></header>
                    {d.documents.length === 0 ? (
                      <p className="cp-empty">No documents issued yet.</p>
                    ) : (
                      <ul className="cp-docs">
                        {d.documents.map(doc => (
                          <li key={doc.id}>
                            <FileText size={14} />
                            <div>
                              <strong>{DOCUMENT_LABEL[doc.kind] ?? doc.name}</strong>
                              <small>{doc.name}</small>
                              {/* Only when there is somewhere to go. A signed
                                  agreement one click away beats an email search. */}
                              {doc.externalUrl && (
                                <a href={doc.externalUrl} target="_blank" rel="noreferrer">Open</a>
                              )}
                            </div>
                            <em data-status={doc.status}>{doc.status.replace(/_/g, " ")}</em>
                          </li>
                        ))}
                      </ul>
                    )}
                  </section>
                )}

                {v.billing && (
                  <section className="cp-card" id="billing">
                    <header><h2>Invoices</h2></header>
                    {!d.standing || d.standing.invoices.length === 0 ? (
                      <p className="cp-empty">No open invoices.</p>
                    ) : (
                      <ul className="cp-invoices">
                        {d.standing.invoices.map(inv => (
                          <li key={inv.id} data-late={inv.daysPastDue > 0 || undefined}>
                            <div><strong>{inv.description}</strong><small>Due {date(inv.dueAt)}{inv.daysPastDue > 0 ? ` · ${inv.daysPastDue} days past due` : ""}</small></div>
                            <em>{money(inv.balanceCents)}</em>
                          </li>
                        ))}
                      </ul>
                    )}
                  </section>
                )}

                {/* Autopay. Its own panel as well as the banner: the banner
                    goes away once verified, and the method on file is
                    something a client wants to check without it. */}
                {v.manageBilling && (
                  <section className="cp-card" id="autopay">
                    <header><h2>Autopay</h2></header>
                    {d.client.paymentMethodVerified ? (
                      <>
                        <p className="cp-autopay">
                          <Landmark size={15} />
                          <span>
                            <strong>
                              {d.client.paymentMethodKind === "us_bank_account" ? "Bank debit" : "Card"} ending {d.client.paymentMethodLast4 ?? "****"}
                            </strong>
                            <small>Verified and collecting invoices on their due date.</small>
                          </span>
                        </p>
                        <button
                          type="button"
                          className="button button--outline-dark button--full"
                          disabled={billingPortal.isPending}
                          onClick={() => billingPortal.mutate()}
                        >
                          {billingPortal.isPending ? "Opening…" : "Change payment method"}
                        </button>
                      </>
                    ) : (
                      <>
                        <p>
                          Bank debit is preferred for management fees — it costs a fraction of card
                          processing and does not fail when a card is reissued. A card can be used instead.
                        </p>
                        <p className="cp-note">
                          If your bank cannot be connected instantly, Stripe sends two small deposits to
                          confirm the account. Verification completes when those are confirmed, usually
                          within three business days, and services begin then.
                        </p>
                        <button
                          type="button"
                          className="button button--coral button--full"
                          disabled={startAutopay.isPending}
                          onClick={() => startAutopay.mutate()}
                        >
                          {startAutopay.isPending ? "Opening Stripe…" : "Set up autopay"}
                        </button>
                      </>
                    )}
                  </section>
                )}

                {/* The snapshot. Shipped as a staff-entered feed, with the
                    source stated so nobody reads a typed figure as a bank one. */}
                {v.financialSnapshot && (
                  <section className="cp-card">
                    <header><h2>Financial snapshot</h2></header>
                    {!d.financials?.latest ? (
                      <p className="cp-empty">
                        No periods recorded yet. Once your figures are loaded this is where revenue,
                        membership and trend numbers appear.
                      </p>
                    ) : (
                      <>
                        <dl className="cp-snapshot">
                          <div>
                            <dt>Revenue</dt>
                            <dd>
                              {money(d.financials.latest.revenueCents)}
                              {d.financials.revenueChangePercent !== null && (
                                <em data-down={d.financials.revenueChangePercent < 0 || undefined}>
                                  {d.financials.revenueChangePercent < 0 ? <TrendingDown size={12} /> : <TrendingUp size={12} />}
                                  {Math.abs(d.financials.revenueChangePercent)}%
                                </em>
                              )}
                            </dd>
                          </div>
                          <div>
                            <dt>Net</dt>
                            <dd>
                              {money(d.financials.latest.netCents)}
                              {d.financials.latest.marginPercent !== null && <em>{d.financials.latest.marginPercent}% margin</em>}
                            </dd>
                          </div>
                          <div>
                            <dt>Active members</dt>
                            <dd>
                              {d.financials.latest.activeMembers ?? "—"}
                              {d.financials.memberChangePercent !== null && (
                                <em data-down={d.financials.memberChangePercent < 0 || undefined}>
                                  {d.financials.memberChangePercent < 0 ? <TrendingDown size={12} /> : <TrendingUp size={12} />}
                                  {Math.abs(d.financials.memberChangePercent)}%
                                </em>
                              )}
                            </dd>
                          </div>
                          <div>
                            <dt>Joins / cancels</dt>
                            <dd>
                              {d.financials.latest.newMembers ?? "—"} / {d.financials.latest.cancellations ?? "—"}
                              {d.financials.latest.churnPercent !== null && <em>{d.financials.latest.churnPercent}% churn</em>}
                            </dd>
                          </div>
                        </dl>
                        <small className="cp-snapshot__source">
                          Period ending {date(d.financials.latest.periodEnd)}
                          {d.financials.manualOnly ? " · entered by your engagement lead" : " · from your connected systems"}
                        </small>
                      </>
                    )}
                  </section>
                )}

                {/* Notes. A two-way thread, not an announcement feed - which
                    is why it carries a compose box and the updates panel
                    above does not. */}
                {v.notes && (
                  <section className="cp-card" id="notes">
                    <header><h2>Notes</h2></header>
                    {d.notes.length === 0 ? (
                      <p className="cp-empty">
                        <MessageSquare size={15} /> No notes yet. Anything you write here goes
                        straight to your engagement lead.
                      </p>
                    ) : (
                      <ul className="cp-notes">
                        {d.notes.map(note => (
                          <li key={note.id} data-from={note.fromClient ? "client" : "altitude"}>
                            <div><strong>{note.authorName}</strong><small>{date(note.createdAt)}</small></div>
                            <p>{note.body}</p>
                          </li>
                        ))}
                      </ul>
                    )}
                    <form
                      className="cp-notes__compose"
                      onSubmit={event => {
                        event.preventDefault();
                        const form = event.currentTarget;
                        const body = String(new FormData(form).get("body") ?? "").trim();
                        if (!body) return;
                        postNote.mutate({ body });
                        form.reset();
                      }}
                    >
                      <label className="field">
                        <span className="sr-only">Add a note</span>
                        <textarea name="body" rows={3} maxLength={4000} placeholder="Leave a note for your engagement lead…" />
                      </label>
                      <button className="button button--coral" disabled={postNote.isPending}>
                        {postNote.isPending ? "Sending…" : "Send note"}
                      </button>
                    </form>
                  </section>
                )}
              </div>
            </div>

            {/* The marketing calendar, full width - a month grid in a sidebar
                column is unreadable on anything narrower than a desktop. */}
            <section className="cp-card cp-card--wide" id="calendar">
              <header>
                <h2>Marketing &amp; social calendar</h2>
                {d.upcomingCampaigns.length > 0 && (
                  <span className="cp-count">
                    {d.upcomingCampaigns.length} coming up
                  </span>
                )}
              </header>
              <MarketingCalendar
                campaigns={d.campaigns}
                editable={v.manageMarketing}
                busy={saveCampaign.isPending || updateCampaign.isPending || deleteCampaign.isPending}
                onCreate={draft => saveCampaign.mutate(draft)}
                onUpdate={(id, patch) => updateCampaign.mutate({ id, patch })}
                onDelete={id => deleteCampaign.mutate({ id })}
              />
            </section>
          </div>
        </section>
      </div>
    </SiteLayout>
  );
}

/**
 * Accept or decline one suggestion.
 *
 * Decline asks for a reason before it will submit; accept does not. The
 * asymmetry is deliberate: "no, because we tried that last summer" is the
 * information Altitude needs in order not to suggest it again, and an
 * unexplained decline teaches nobody anything. Accepting speaks for itself.
 */
function SuggestionAnswer({
  busy,
  onAnswer,
}: {
  busy: boolean;
  onAnswer: (status: "accepted" | "declined", response?: string) => void;
}) {
  const [declining, setDeclining] = useState(false);
  const [reason, setReason] = useState("");

  if (!declining) {
    return (
      <div className="cp-suggestions__actions">
        <button type="button" className="button button--coral" disabled={busy} onClick={() => onAnswer("accepted")}>
          Accept
        </button>
        <button type="button" className="button button--outline-dark" disabled={busy} onClick={() => setDeclining(true)}>
          Decline
        </button>
      </div>
    );
  }

  return (
    <div className="cp-suggestions__decline">
      <label className="field">
        <span>Why not? <em>So we don't suggest it again.</em></span>
        <textarea
          rows={2}
          maxLength={4000}
          value={reason}
          autoFocus
          onChange={event => setReason(event.target.value)}
        />
      </label>
      <div className="cp-suggestions__actions">
        <button
          type="button"
          className="button button--coral"
          disabled={busy || reason.trim().length === 0}
          onClick={() => onAnswer("declined", reason.trim())}
        >
          {busy ? "Sending…" : "Send decline"}
        </button>
        <button type="button" className="button button--outline-dark" onClick={() => { setDeclining(false); setReason(""); }}>
          Cancel
        </button>
      </div>
    </div>
  );
}
